import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Paper, IconButton, Tooltip, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useTranslation } from 'react-i18next';
import { useCall } from './CallContext';
import { useCallTimer } from './useCallTimer';

function VideoView({ stream, isSelf }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const v = ref.current;
    if (v && stream) {
      v.srcObject = stream;
      // Only mute self preview; allow remote audio to play
      v.muted = !!isSelf;
      const tryPlay = () => v.play().catch((err) => {
        if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
          const resume = () => {
            v.play().catch(() => {});
            window.removeEventListener('click', resume);
            window.removeEventListener('touchend', resume);
          };
          window.addEventListener('click', resume, { once: true });
          window.addEventListener('touchend', resume, { once: true });
        }
      });
      try { tryPlay(); } catch (_) {}
    }
  }, [stream, isSelf]);
  return (
    <video ref={ref} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }} />
  );
}

export default function MiniCall() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeRoomId, state, actions } = useCall() || {};
  const { participants } = state || {};
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const { label: timerLabel, active: timerActive } = useCallTimer();

  // Determine visibility; do not early-return before hooks
  const onCallPage = /(^|\/)call(\/|$)/.test(location.pathname || '');
  const shouldShow = !!activeRoomId && !onCallPage;

  // Pick a stream to render (prefer self, else first participant with stream)
  const streams = React.useMemo(() => {
    const list = Object.values(participants || {});
    const self = list.find(p => p && p.isSelf && p.stream);
    const other = list.find(p => p && !p.isSelf && p.stream);
    return { self: self?.stream || null, other: other?.stream || null };
  }, [participants]);
  const streamToShow = streams.self || streams.other || null;
  const isSelfStream = !!streams.self;

  // Mic/cam states inferred from track.enabled
  const [micOn, setMicOn] = React.useState(true);
  const [camOn, setCamOn] = React.useState(true);
  React.useEffect(() => {
    try {
      const self = participants?.self;
      if (self?.stream) {
        const a = self.stream.getAudioTracks();
        const v = self.stream.getVideoTracks();
        if (a && a[0]) setMicOn(!!a[0].enabled);
        if (v && v[0]) setCamOn(!!v[0].enabled);
      }
    } catch(_) {}
  }, [participants]);

  // Simple draggable position state (hooks must be declared before any conditional return)
  const [pos, setPos] = React.useState(() => {
    // default bottom-right
    return { right: 16, bottom: 16 };
  });
  const dragData = React.useRef(null);

  if (!shouldShow) return null;

  const onMouseDown = (e) => {
    const el = e.currentTarget.closest('[data-mini-call]');
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragData.current = { startX: e.clientX, startY: e.clientY, startLeft: rect.left, startTop: rect.top, width: rect.width, height: rect.height };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp, { once: true });
    e.preventDefault();
  };
  const onMouseMove = (e) => {
    const d = dragData.current; if (!d) return;
    const dx = e.clientX - d.startX; const dy = e.clientY - d.startY;
    let left = d.startLeft + dx; let top = d.startTop + dy;
    // constrain to viewport
    const vw = window.innerWidth; const vh = window.innerHeight;
    left = Math.min(Math.max(0, left), vw - d.width);
    top = Math.min(Math.max(0, top), vh - d.height);
    // convert to {left, top}
    setPos({ left, top });
  };
  const onMouseUp = () => {
    window.removeEventListener('mousemove', onMouseMove);
    dragData.current = null;
  };

  const handleExpand = () => {
    navigate(`/call/${encodeURIComponent(activeRoomId)}`);
  };

  const confirmEnd = async () => {
    setConfirmOpen(true);
  };

  const handleConfirmEnd = () => {
    try { actions.leaveAndClear?.(); } catch(_) { try { actions.leave?.(); } catch(_) {} }
    setConfirmOpen(false);
  };

  const handleCancelEnd = () => setConfirmOpen(false);

  const toggleMic = () => setMicOn(prev => actions.toggleMic ? actions.toggleMic(!prev) : !prev);
  const toggleCam = () => setCamOn(prev => actions.toggleCamera ? actions.toggleCamera(!prev) : !prev);

  return (
    <>
      <Paper data-mini-call elevation={0} sx={{
        position: 'fixed', zIndex: 2000,
        width: { xs: 200, sm: 260 }, height: { xs: 130, sm: 170 },
        borderRadius: 4, overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.15)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.1)',
        transition: 'box-shadow 0.3s ease',
        '&:hover': { boxShadow: '0 16px 48px rgba(0,0,0,0.4), 0 0 0 1px rgba(99,102,241,0.3)' },
        ...(pos.left != null ? { left: pos.left, top: pos.top } : { right: pos.right, bottom: pos.bottom })
      }}>
        <Box onMouseDown={onMouseDown} sx={{ position: 'relative', width: '100%', height: '100%', cursor: 'move' }}>
          {streamToShow ? (
            <VideoView stream={streamToShow} isSelf={isSelfStream} />
          ) : (
            <Box sx={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption">{t('call.reconnecting', 'Reconnecting...')}</Typography>
            </Box>
          )}
          {/* Top gradient for better readability */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 36, background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, transparent 100%)', pointerEvents: 'none' }} />
          {/* Timer badge */}
          {timerActive && (
            <Box onMouseDown={(e)=>e.stopPropagation()} sx={{ position: 'absolute', top: 6, left: 6 }}>
              <Typography variant="caption" sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: '#fff',
                px: 1,
                py: 0.25,
                borderRadius: 1.5,
                fontWeight: 700,
                fontSize: 11,
                boxShadow: '0 2px 8px rgba(99,102,241,0.3)'
              }}>
                {timerLabel}
              </Typography>
            </Box>
          )}
          {/* Controls bar */}
          <Box onMouseDown={(e)=>e.stopPropagation()} sx={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            px: 1, py: 0.75,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.6) 100%)',
          }}>
            <Tooltip title={micOn ? t('call.mute','Mute') : t('call.unmute','Unmute')}>
              <IconButton size="small" onClick={toggleMic} sx={{
                width: 30, height: 30,
                bgcolor: micOn ? 'rgba(255,255,255,0.15)' : 'rgba(244,67,54,0.8)',
                color: '#fff',
                backdropFilter: 'blur(8px)',
                '&:hover': { bgcolor: micOn ? 'rgba(255,255,255,0.25)' : 'rgba(244,67,54,0.9)' }
              }}>
                {micOn ? <MicIcon sx={{ fontSize: 16 }}/> : <MicOffIcon sx={{ fontSize: 16 }}/>}
              </IconButton>
            </Tooltip>
            <Tooltip title={camOn ? t('call.cameraOff','Camera off') : t('call.cameraOn','Camera on')}>
              <IconButton size="small" onClick={toggleCam} sx={{
                width: 30, height: 30,
                bgcolor: camOn ? 'rgba(255,255,255,0.15)' : 'rgba(244,67,54,0.8)',
                color: '#fff',
                backdropFilter: 'blur(8px)',
                '&:hover': { bgcolor: camOn ? 'rgba(255,255,255,0.25)' : 'rgba(244,67,54,0.9)' }
              }}>
                {camOn ? <VideocamIcon sx={{ fontSize: 16 }}/> : <VideocamOffIcon sx={{ fontSize: 16 }}/>}
              </IconButton>
            </Tooltip>
            <Tooltip title={t('call.expand','Expand')}>
              <IconButton size="small" onClick={handleExpand} sx={{
                width: 30, height: 30,
                bgcolor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                backdropFilter: 'blur(8px)',
                '&:hover': { bgcolor: 'rgba(99,102,241,0.6)' }
              }}>
                <OpenInFullIcon sx={{ fontSize: 16 }}/>
              </IconButton>
            </Tooltip>
            <Tooltip title={t('call.leave','Leave')}>
              <IconButton size="small" onClick={confirmEnd} sx={{
                width: 30, height: 30,
                bgcolor: 'rgba(244,67,54,0.8)',
                color: '#fff',
                backdropFilter: 'blur(8px)',
                '&:hover': { bgcolor: 'rgba(244,67,54,0.95)' }
              }}>
                <CallEndIcon sx={{ fontSize: 16 }}/>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Dialog open={confirmOpen} onClose={handleCancelEnd} fullWidth maxWidth="xs">
        <DialogTitle>{t('call.leave','Leave')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('call.endConfirm', 'End the call for you?')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEnd}>{t('common.cancel','Cancel')}</Button>
          <Button onClick={handleConfirmEnd} color="error" variant="contained">{t('call.leave','Leave')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
